import { useFormik } from 'formik';
import React from 'react';
import { Button, Input, Modal, ModalBody } from 'reactstrap';
import * as Yup from 'yup';

type CreateAccountUnofficialModalProps = {
  show: boolean;
  unofficialStudentId: string | undefined;
  onCloseClick: () => void;
  onApplyFilters: (information: {
    inputEmail: string;
    inputStudentId: string;
    inputFirstName: string;
    inputLastName?: string | undefined;
    inputGender: string;
    inputPhone: string;
  }) => Promise<void>;
};

const CreateAccountUnofficialModal = ({
  show,
  unofficialStudentId,
  onCloseClick,
  onApplyFilters,
}: CreateAccountUnofficialModalProps) => {
  const formik = useFormik({
    initialValues: {
      email: '',
      studentId: unofficialStudentId || '',
      firstName: '',
      lastName: undefined,
      gender: '',
      phone: '',
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      email: Yup.string().email('Please enter a valid email address').required('Email is required'),
      studentId: Yup.number()
        .typeError('Student ID must be a number')
        .required('Student ID is required'),
      firstName: Yup.string().required('First name is required'),
      gender: Yup.string()
        .required('Gender is required')
        .test('is-valid-gender', 'Gender must be one of: Nam, Nữ, Khác', (value) => {
          const gender = value?.trim().toLowerCase();
          return gender === 'nam' || gender === 'nữ' || gender === 'khác';
        }),
      phone: Yup.string()
        .matches(/^\d+$/, 'Phone must be a number')
        .required('Phone is required')
        .min(10, 'Phone must be at least 10 digits')
        .max(11, 'Phone can b at most 11 digits'),
    }),
    onSubmit: (values) => {
      let trimmed = values.gender.trim().toLowerCase();
      trimmed =
        trimmed === 'nam' ? 'Nam' : trimmed === 'nữ' ? 'Nữ' : trimmed === 'khác' ? 'Khác' : trimmed;
      onApplyFilters({
        inputEmail: values.email,
        inputStudentId: values.studentId,
        inputFirstName: values.firstName,
        inputLastName: values?.lastName ? values.lastName : '',
        inputGender: trimmed,
        inputPhone: values.phone,
      });
    },
  });

  return (
    <Modal
      isOpen={show}
      toggle={() => {}}
      backdrop='static'
      keyboard={false}
      centered
      size='lg'
      className='tw-rounded-[0.5rem]'
    >
      <ModalBody>
        <button
          type='button'
          onClick={onCloseClick}
          className='btn-close position-absolute end-0 top-0 m-3'
          aria-label='Close'
        ></button>

        <form className='tw-flex tw-flex-col tw-gap-y-5 tw-items-start'>
          <div>
            <p className='tw-font-bold tw-text-primary tw-text-[20px] md:tw-text-[24px] lg:tw-text-[28px] tw-mb-4'>
              Create official account for unofficial participants
            </p>
            <p className='tw-font-medium tw-text-[#3D4863] tw-text-[14px] md:tw-text-md'>
              Please enter these information:
            </p>
          </div>
          <div className='tw-flex tw-flex-col tw-gap-y-6 tw-items-center tw-justify-start tw-w-full'>
            <div className='tw-flex tw-flex-row tw-gap-x-4 md:tw-gap-x-8 tw-justify-stretch tw-items-center tw-w-full'>
              <h2 className='tw-font-bold tw-text-primary tw-text-[14px] md:tw-text-[16px] tw-mb-0 tw-min-w-[80px] md:tw-min-w-[90px]'>
                Email:
              </h2>
              <div className='tw-flex tw-self-center tw-flex-col tw-justify-start tw-w-full'>
                <Input
                  id='email'
                  className='form-control'
                  name='email'
                  type='text'
                  placeholder="Please enter student's email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email || ''}
                  invalid={formik.touched.email && formik.errors.email ? true : false}
                />
                {formik.touched.email && formik.errors.email ? (
                  <p className='tw-text-[12px] tw-text-[#EA4335] tw-font-normal tw-mb-0'>
                    {formik.errors.email}
                  </p>
                ) : null}
              </div>
            </div>
            <div className='tw-flex tw-flex-row tw-gap-x-4 md:tw-gap-x-8 tw-justify-stretch tw-items-center tw-w-full'>
              <h2 className='tw-font-bold tw-text-primary tw-text-[14px] md:tw-text-[16px] tw-mb-0 tw-min-w-[80px] md:tw-min-w-[90px]'>
                Student's Id:
              </h2>
              <div className='tw-flex tw-flex-col tw-justify-start tw-w-full'>
                <Input
                  id='studentId'
                  className='form-control'
                  name='studentId'
                  type='text'
                  placeholder="Please enter student's id"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.studentId || ''}
                  invalid={formik.touched.studentId && formik.errors.studentId ? true : false}
                />
                {formik.touched.studentId && formik.errors.studentId ? (
                  <p className='tw-text-[12px] tw-text-[#EA4335] tw-font-normal tw-mb-0'>
                    {formik.errors.studentId}
                  </p>
                ) : null}
              </div>
            </div>
            <div className='tw-flex tw-flex-row tw-gap-x-4 md:tw-gap-x-8 tw-justify-stretch tw-items-center tw-w-full'>
              <h2 className='tw-font-bold tw-text-primary tw-text-[14px] md:tw-text-[16px] tw-mb-0 tw-min-w-[80px] md:tw-min-w-[90px]'>
                First Name:
              </h2>
              <div className='tw-flex tw-flex-col tw-justify-start tw-w-full'>
                <Input
                  id='firstName'
                  className='form-control'
                  name='firstName'
                  type='text'
                  placeholder="Please enter student's first name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.firstName || ''}
                  invalid={formik.touched.firstName && formik.errors.firstName ? true : false}
                />
                {formik.touched.firstName && formik.errors.firstName ? (
                  <p className='tw-text-[12px] tw-text-[#EA4335] tw-font-normal tw-mb-0'>
                    {formik.errors.firstName}
                  </p>
                ) : null}
              </div>
            </div>
            <div className='tw-flex tw-flex-row tw-gap-x-4 md:tw-gap-x-8 tw-justify-stretch tw-items-center tw-w-full'>
              <h2 className='tw-font-bold tw-text-primary tw-text-[14px] md:tw-text-[16px] tw-mb-0 tw-min-w-[80px] md:tw-min-w-[90px]'>
                Last Name:
              </h2>
              <div className='tw-flex tw-flex-col tw-justify-start tw-w-full'>
                <Input
                  id='lastName'
                  className='form-control'
                  name='lastName'
                  type='text'
                  placeholder="Please enter student's last name (optional)"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.lastName || ''}
                  invalid={formik.touched.lastName && formik.errors.lastName ? true : false}
                />
                {formik.touched.lastName && formik.errors.lastName ? (
                  <p className='tw-text-[12px] tw-text-[#EA4335] tw-font-normal tw-mb-0'>
                    {formik.errors.lastName}
                  </p>
                ) : null}
              </div>
            </div>
            <div className='tw-flex tw-flex-row tw-gap-x-4 md:tw-gap-x-8 tw-justify-stretch tw-items-center tw-w-full'>
              <h2 className='tw-font-bold tw-text-primary tw-text-[14px] md:tw-text-[16px] tw-mb-0 tw-min-w-[80px] md:tw-min-w-[90px]'>
                Gender:
              </h2>
              <div className='tw-flex tw-flex-col tw-justify-start tw-w-full'>
                <Input
                  id='gender'
                  className='form-control'
                  name='gender'
                  type='text'
                  placeholder='Nam /  Nữ /  Khác'
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.gender || ''}
                  invalid={formik.touched.email && formik.errors.gender ? true : false}
                />
                {formik.touched.gender && formik.errors.gender ? (
                  <p className='tw-text-[12px] tw-text-[#EA4335] tw-font-normal tw-mb-0'>
                    {formik.errors.gender}
                  </p>
                ) : null}
              </div>
            </div>
            <div className='tw-flex tw-flex-row tw-gap-x-4 md:tw-gap-x-8 tw-justify-stretch tw-items-center tw-w-full'>
              <h2 className='tw-font-bold tw-text-primary tw-text-[14px] md:tw-text-[16px] tw-mb-0 tw-min-w-[80px] md:tw-min-w-[90px]'>
                Phone:
              </h2>
              <div className='tw-flex tw-flex-col tw-justify-start tw-w-full'>
                <Input
                  id='phone'
                  className='form-control'
                  name='phone'
                  type='text'
                  placeholder="Please enter student's phone"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.phone || ''}
                  invalid={formik.touched.phone && formik.errors.phone ? true : false}
                />
                {formik.touched.phone && formik.errors.phone ? (
                  <p className='tw-text-[12px] tw-text-[#EA4335] tw-font-normal tw-mb-0'>
                    {formik.errors.phone}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className='tw-flex tw-flex-row tw-w-full tw-justify-end tw-items-center tw-gap-x-4'>
            <Button type='button' color='primary' outline onClick={onCloseClick}>
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={() => formik.handleSubmit()}
              className='tw-px-6 tw-w-fit tw-py-2 tw-rounded-md tw-font-medium'
            >
              Confirm and Transfer
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default CreateAccountUnofficialModal;
